/**
 * Converts a string to Title Case (each word capitalized).
 * @param {string} value
 * @returns {string}
 */
export const toTitleCase = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  return value
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Corrects known menu title typos from the API before display.
 * @param {string} title - Raw menu title from nested_menus
 * @returns {string} - Corrected menu title
 */
export const formatMenuTitle = (title) => {
  if (!title || typeof title !== 'string') {
    return title;
  }

  const corrections = {
    'Apprasial Mange': 'Appraisal Management',
    'Apprasial Manage': 'Appraisal Management',
    'Appraisal Manage': 'Appraisal Management',
    'Appraisal Mange': 'Appraisal Management',
    'Monthly Apprasials': 'Monthly Appraisals',
    'Quarterly Apprasials': 'Quarterly Appraisals',
    'Yearly Apprasials': 'Yearly Appraisals',
    'Probation Apprasials': 'Probationary Appraisals',
    'Probation Appraisals': 'Probationary Appraisals',
    'Probational Appraisals': 'Probationary Appraisals',
    'My Pafs': 'My PAFs',
    'Paf Calendar': 'PAF Calendar',
    'Paf Engine': 'PAF Settings',
    'Paf Settings': 'PAF Settings',
    'Email Settings': 'Email Settings',
    'Email Templates': 'Email Templates',
    'Email Reminders': 'Email Reminders',
    'Mail Logs': 'Mail Logs',
    'SMTP Configuration': 'SMTP Configuration',
    'Smtp Configuration': 'SMTP Configuration',
  };

  const corrected = corrections[title];
  if (corrected) {
    return corrected;
  }

  return toTitleCase(title);
};

/**
 * Recursively searches nested_menus to find a menu item that matches the given URL
 * @param {Array} menus - Array of menu objects from nested_menus
 * @param {string} url - The URL to match (can be full pathname or relative path)
 * @returns {Object|null} - The matched menu item with id field, or null if not found
 */
export const findMenuItemByUrl = (menus, url) => {
  if (!menus || !Array.isArray(menus) || !url) {
    return null;
  }

  // Normalize the URL - remove leading slash and handle both formats
  const normalizedUrl = url.replace(/^\//, '').replace(/\/$/, '');
  
  for (const item of menus) {
    // Check if this item matches by object_url or href
    const itemUrl = item.object_url || item.href || '';
    const normalizedItemUrl = itemUrl.replace(/^\//, '').replace(/\/$/, '');
    
    // Exact match
    if (normalizedItemUrl === normalizedUrl) {
      return item;
    }
    
    // Partial match - check if current URL starts with item URL (for nested routes)
    // e.g., '/appraisalSetup/add' should match '/appraisalSetup'
    if (normalizedItemUrl && normalizedUrl.startsWith(normalizedItemUrl)) {
      // Additional check: ensure it's a proper path segment match
      const nextChar = normalizedUrl[normalizedItemUrl.length];
      if (!nextChar || nextChar === '/') {
        return item;
      }
    }
    
    // Recursively search children
    if (item.children && Array.isArray(item.children) && item.children.length > 0) {
      const found = findMenuItemByUrl(item.children, url);
      if (found) {
        return found;
      }
    }
  }
  
  return null;
};

/**
 * Nested scoring/view routes map to the parent Access Management menu object_url.
 * Keep in sync with NavigationListItem VIEW_ROUTE_ALIASES.
 */
export const ACCESS_OBJECT_ROUTE_ALIASES = {
  appraisalprogressform: "appraisal",
  monthlyappraisalview: "appraisal",
  monthlyappraisalreport: "monthly-reports",
  "quarterly-appraisal/paf": "quarterly-appraisal",
  "quarterly-appraisal/view": "quarterly-appraisal",
  quarterlyappraisalview: "quarterly-appraisal",
  "yearly-appraisals/paf": "yearly-appraisals",
  "yearly-appraisals/view": "yearly-appraisals",
  yearlyappraisalview: "yearly-appraisals",
  "probationary-appraisals/view": "probationary-appraisals",
  "probational-appraisals/view": "probational-appraisals",
};

/**
 * Resolve a UI path to the Access Management object_url (no leading slash).
 * Form routes inherit the list page via `fromPath` when present.
 */
export const resolveAccessObjectPath = (pathname, fromPath = null) => {
  if (!pathname || typeof pathname !== "string") {
    return "";
  }

  let url = pathname.replace(/^\//, "").replace(/\/$/, "");
  const parts = url.split("/").filter(Boolean);
  const last = parts[parts.length - 1];

  if (["add", "update", "edit", "view"].includes(last)) {
    parts.pop();
    url = parts.join("/");
  }

  const formBase = url.split("/")[0];
  if (formBase === "appraisalprogressform" && fromPath) {
    const fromResolved = resolveAccessObjectPath(fromPath, null);
    if (fromResolved) {
      return fromResolved;
    }
  }

  if (ACCESS_OBJECT_ROUTE_ALIASES[url]) {
    return ACCESS_OBJECT_ROUTE_ALIASES[url];
  }

  for (const [alias, target] of Object.entries(ACCESS_OBJECT_ROUTE_ALIASES)) {
    if (url === alias || url.startsWith(`${alias}/`)) {
      return target;
    }
  }

  return url;
};

/**
 * Extracts object_viewed_id from nested_menus based on current route
 * @param {Array} nested_menus - Array of menu objects from Redux state
 * @param {string} currentPath - Current route pathname (e.g., '/appraisalSetup/add')
 * @param {string|null} fromPath - Origin list path (e.g. '/quarterly-appraisal')
 * @returns {string|null} - The object_viewed_id (menu item id) or null if not found
 */
export const getObjectViewedIdFromRoute = (nested_menus, currentPath, fromPath = null) => {
  if (!nested_menus || !currentPath) {
    return null;
  }

  const resolvedPath = resolveAccessObjectPath(currentPath, fromPath);
  const matchedMenu = findMenuItemByUrl(
    nested_menus,
    resolvedPath.startsWith("/") ? resolvedPath : `/${resolvedPath}`
  );
  return matchedMenu ? matchedMenu.id : null;
};

/**
 * Gets permissions for a specific object_viewed_id from user_permissions_raw
 * @param {Object|Array} user_permissions_raw - Permissions object/array from Redux state
 * @param {string} object_viewed_id - The object_viewed_id to get permissions for
 * @returns {Object|null} - Permission object with rights_add, rights_edit, rights_delete, rights_view, or null
 */
export const getPermissionsByObjectViewedId = (user_permissions_raw, object_viewed_id) => {
  if (!user_permissions_raw || !object_viewed_id) {
    return null;
  }
  
  // Handle both array and object formats
  if (Array.isArray(user_permissions_raw)) {
    // If it's an array, find by object_id or object_viewed_id
    const permission = user_permissions_raw.find(
      item => item.object_id === object_viewed_id || item.object_viewed_id === object_viewed_id
    );
    return permission || null;
  } else if (typeof user_permissions_raw === 'object') {
    // If it's an object, access by key
    return user_permissions_raw[object_viewed_id] || null;
  }
  
  return null;
};
